import moment from "moment";

// Dummy enquiries for the demo Leads page — generated, never stored. A fixed
// seed keeps the same leads across reloads; dates are relative to today so
// the demo always has fresh "today" and "overdue" rows.

export const LEAD_SOURCES = [
  { key: "facebook", label: "Facebook", short: "f", color: "#1877F2" },
  { key: "instagram", label: "Instagram", short: "IG", color: "#D62976" },
  { key: "google", label: "Google Ads", short: "G", color: "#EA4335" },
  { key: "justdial", label: "JustDial", short: "JD", color: "#F7931E" },
  { key: "practo", label: "Practo", short: "Pr", color: "#14BEF0" },
  { key: "website", label: "Website form", short: "W", color: "#6D28D9" },
  { key: "phone", label: "Phone call", short: "☎", color: "#0F766E" },
  { key: "walkin", label: "Walk-in", short: "WI", color: "#475569" },
  { key: "referral", label: "Referral", short: "R", color: "#16A34A" },
];
export const SOURCE_BY_KEY = Object.fromEntries(LEAD_SOURCES.map((s) => [s.key, s]));

export const LEAD_STATUSES = [
  { key: "new", label: "New", variant: "info" },
  { key: "contacted", label: "Contacted", variant: "secondary" },
  { key: "follow_up", label: "Follow-up", variant: "warning" },
  { key: "booked", label: "Appointment booked", variant: "default" },
  { key: "converted", label: "Converted", variant: "success" },
  { key: "lost", label: "Lost", variant: "outline" },
  { key: "junk", label: "Junk", variant: "destructive" },
];
export const STATUS_BY_KEY = Object.fromEntries(LEAD_STATUSES.map((s) => [s.key, s]));

export const LEAD_SERVICES = [
  "Skin Clinic",
  "Dental",
  "Orthopaedics",
  "Diagnostics / Lab",
  "Gynaecology",
  "General Physician",
  "Full Body Checkup",
  "Paediatrics",
  "ENT",
];

export const LEAD_STAFF = ["Neha Das", "Priyanka Bora", "Rahul Kalita", "Ankita Sharma", "Unassigned"];

export const LOST_REASONS = [
  "Not responding",
  "Price too high",
  "Treatment not available",
  "Booked elsewhere",
  "Only enquiring",
  "Wrong number",
];

const CAMPAIGNS = {
  facebook: ["FB – Skin Glow Oct", "FB – Full Body Checkup ₹999", "FB – Dental Smile Week"],
  instagram: ["IG – Laser Hair Reduction", "IG – Acne Clinic Reels", "IG – Diwali Health Pack"],
  google: ["Google – Orthopaedic Guwahati", "Google – Diagnostic Centre Near Me", "Google – Gynaecologist Ulubari"],
  justdial: ["JustDial listing"],
  practo: ["Practo profile"],
};

const FIRST = [
  "Ankita", "Bhaskar", "Chandana", "Debajit", "Gitanjali", "Hiranya", "Jyotishmita", "Kaustav", "Lakhimi", "Manash",
  "Nabanita", "Pallavi", "Rituparna", "Sanjib", "Trishna", "Upasana", "Dhruba", "Priyanka", "Rajdeep", "Monalisa",
  "Arnab", "Bornali", "Deepjyoti", "Juri", "Kangkana", "Mridul", "Nilakshi", "Parag", "Rumi", "Sourav",
  "Abhinav", "Mitraa", "Rhina", "Saswati", "Munmi", "Minakshi", "Suneel", "Farhana", "Imran", "Nazia",
];
const LAST = [
  "Das", "Bora", "Kalita", "Sharma", "Baruah", "Gogoi", "Deka", "Saikia", "Hazarika", "Choudhury",
  "Bhuyan", "Medhi", "Talukdar", "Nath", "Phukan", "Dutta", "Ahmed", "Rahman", "Sen", "Mahanta",
];
// What social lead forms actually hand over — a handle or a first name only.
const SOCIAL_NAMES = ["CANCERIAN", "Glow_with_Rimi", "J .", "Mitraa B", "Suneel NA", "dr.puja", "Bubu", "Rinki ❤️"];

const NOTES = {
  new: ["Enquiry received — not called yet", "Filled the lead form, wants a call back", "Asked for price list"],
  contacted: ["Explained the package, will confirm", "Spoke to patient — wants Saturday slot", "Shared doctor timings on WhatsApp"],
  follow_up: ["Not responding, send msg", "Call back after 6 PM", "Will decide after salary credit", "Asked to call next week"],
  booked: ["Appointment booked for Saturday 11 AM", "Booked with Dr Sarma, Tuesday", "Booked full body checkup for tomorrow"],
  converted: ["Visited and completed consultation", "Came for checkup, reports delivered", "Started treatment — 4 sittings"],
  lost: ["Treatment not available (belly fat)", "Booked at another clinic", "Price too high"],
  junk: ["No enquiry done by him, not interested", "Wrong number", "Test lead"],
};

// Mulberry32 — tiny deterministic PRNG.
const rng = (seed) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const weighted = (rand, entries) => {
  const total = entries.reduce((s, [, w]) => s + w, 0);
  let r = rand() * total;
  for (const [v, w] of entries) if ((r -= w) < 0) return v;
  return entries[entries.length - 1][0];
};

export function generateDemoLeads(count = 160) {
  const rand = rng(20261023);
  const pick = (arr) => arr[Math.floor(rand() * arr.length)];
  const leads = [];

  for (let i = 0; i < count; i++) {
    const source = weighted(rand, [
      ["facebook", 26], ["instagram", 18], ["google", 14], ["justdial", 14], ["practo", 6],
      ["website", 8], ["phone", 7], ["walkin", 4], ["referral", 3],
    ]);
    const status = weighted(rand, [
      ["new", 14], ["contacted", 14], ["follow_up", 20], ["booked", 12], ["converted", 16], ["lost", 14], ["junk", 6],
    ]);
    const social = ["facebook", "instagram"].includes(source) && rand() < 0.22;
    const name = social ? pick(SOCIAL_NAMES) : `${pick(FIRST)} ${pick(LAST)}`;
    const daysAgo = status === "new" ? Math.floor(rand() * 3) : Math.floor(rand() * 45);
    const enquiry = moment().subtract(daysAgo, "days").hour(8 + Math.floor(rand() * 12)).minute(Math.floor(rand() * 60));
    const service = source === "practo" ? pick(["Dental", "Gynaecology", "Paediatrics", "ENT"]) : pick(LEAD_SERVICES);
    const owner = status === "new" && rand() < 0.5 ? "Unassigned" : pick(LEAD_STAFF.slice(0, 4));

    let followUp = null;
    if (["new", "contacted", "follow_up"].includes(status)) {
      followUp = moment()
        .add(Math.floor(rand() * 9) - 3, "days")
        .hour(10 + Math.floor(rand() * 8))
        .minute(rand() < 0.5 ? 0 : 30);
    }

    const note = pick(NOTES[status]);
    const phone = `${pick(["6", "7", "8", "9"])}${String(Math.floor(rand() * 1e9)).padStart(9, "0")}`;
    const by = owner === "Unassigned" ? "System" : owner;
    const activity = [
      { at: enquiry.toISOString(), by: "System", text: `Lead captured from ${SOURCE_BY_KEY[source].label}` },
    ];
    if (status !== "new") {
      activity.push({ at: enquiry.clone().add(2, "hours").toISOString(), by, text: "Called — " + (rand() < 0.5 ? "no answer" : "spoke to patient") });
      activity.push({ at: enquiry.clone().add(1, "day").toISOString(), by, text: `Status → ${STATUS_BY_KEY[status].label}` });
    }
    activity.push({ at: enquiry.clone().add(status === "new" ? 0 : 1, "day").add(5, "minutes").toISOString(), by, text: note });

    leads.push({
      id: `L${String(10234 + i)}`,
      name,
      phone,
      email: !social && rand() < 0.35 ? `${name.split(" ")[0].toLowerCase()}${Math.floor(rand() * 90 + 10)}@gmail.com` : "",
      city: weighted(rand, [["Guwahati", 70], ["Dispur", 8], ["Jorhat", 5], ["Nalbari", 5], ["Tezpur", 4], ["Shillong", 4]]),
      source,
      campaign: CAMPAIGNS[source] ? pick(CAMPAIGNS[source]) : "",
      service,
      status,
      owner,
      enquiry_at: enquiry.toISOString(),
      follow_up_at: followUp ? followUp.toISOString() : null,
      last_note: note,
      lost_reason: status === "lost" ? pick(LOST_REASONS) : "",
      activity: activity.sort((a, b) => (a.at < b.at ? 1 : -1)),
    });
  }

  return leads.sort((a, b) => (a.enquiry_at < b.enquiry_at ? 1 : -1));
}
